import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { Plus, ArrowRight, Database, Zap, Send } from "lucide-react";
import StatusPill from "@/components/StatusPill";

export default function Dashboard() {
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);

  const totals = (projects || []).reduce(
    (acc, p) => {
      const s = p.stats || {};
      acc.threads += s.threads_total || 0;
      acc.published += s.threads_published || 0;
      acc.queue += s.publish_queue || 0;
      acc.keywords += s.keywords || 0;
      acc.clicks += s.clicks || 0;
      return acc;
    },
    { threads: 0, published: 0, queue: 0, keywords: 0, clicks: 0 }
  );

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-xs txt-mono uppercase tracking-widest text-muted-foreground">Operator dashboard</div>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">Projects</h1>
          <p className="text-sm text-muted-foreground mt-1">One project = one forum site. Each runs on its own vertical pack.</p>
        </div>
        <Link
          to="/projects/new"
          data-testid={TESTIDS.createProjectBtn}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 text-sm font-medium rounded-sm transition-colors"
        >
          <Plus className="w-4 h-4" /> New project
        </Link>
      </header>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 mb-10 border border-border">
        {[
          { label: "Projects", value: (projects || []).length, icon: Database },
          { label: "Threads total", value: totals.threads, icon: Zap },
          { label: "Published", value: totals.published, icon: Send },
          { label: "Queue", value: totals.queue, icon: ArrowRight },
          { label: "Click events", value: totals.clicks, icon: ArrowRight },
        ].map((s, i) => (
          <div key={s.label} className={`p-4 ${i !== 0 ? "border-l border-border" : ""}`}>
            <div className="text-[10px] txt-mono uppercase tracking-widest text-muted-foreground">{s.label}</div>
            <div className="mt-2 stat-num text-2xl">{s.value}</div>
          </div>
        ))}
      </div>

      {projects === null && <div className="text-sm text-muted-foreground">Loading projects…</div>}
      {projects && projects.length === 0 && (
        <div className="border border-dashed border-border p-10 text-center">
          <h3 className="text-lg font-medium">No projects yet</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Spin up your first Home Services forum to get started.</p>
          <Link to="/projects/new" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-sm rounded-sm">
            <Plus className="w-4 h-4" /> Create first project
          </Link>
        </div>
      )}

      {projects && projects.length > 0 && (
        <div className="border border-border overflow-hidden" data-testid={TESTIDS.projectsTable}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left bg-secondary border-b border-border">
                <th className="px-4 py-2 text-[10px] uppercase tracking-widest txt-mono text-muted-foreground font-medium">Project</th>
                <th className="px-4 py-2 text-[10px] uppercase tracking-widest txt-mono text-muted-foreground font-medium">Pack</th>
                <th className="px-4 py-2 text-[10px] uppercase tracking-widest txt-mono text-muted-foreground font-medium">WP</th>
                <th className="px-4 py-2 text-[10px] uppercase tracking-widest txt-mono text-muted-foreground font-medium">Threads</th>
                <th className="px-4 py-2 text-[10px] uppercase tracking-widest txt-mono text-muted-foreground font-medium">Published</th>
                <th className="px-4 py-2 text-[10px] uppercase tracking-widest txt-mono text-muted-foreground font-medium">Queue</th>
                <th className="px-4 py-2 text-[10px] uppercase tracking-widest txt-mono text-muted-foreground font-medium">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} data-testid={TESTIDS.projectRow} className="border-b border-border last:border-b-0 hover:bg-secondary/60 transition-colors">
                  <td className="px-4 py-3">
                    <Link to={`/projects/${p.id}`} className="font-medium hover:text-primary">{p.name}</Link>
                    <div className="text-xs text-muted-foreground txt-mono truncate max-w-[260px]">{p.domain || "no domain set"}</div>
                  </td>
                  <td className="px-4 py-3 txt-mono text-xs uppercase">{p.pack_slug}</td>
                  <td className="px-4 py-3">
                    {p.wp_configured ? <StatusPill status="active" /> : <span className="text-xs text-muted-foreground">not set</span>}
                  </td>
                  <td className="px-4 py-3 stat-num">{p.stats?.threads_total || 0}</td>
                  <td className="px-4 py-3 stat-num text-emerald-400">{p.stats?.threads_published || 0}</td>
                  <td className="px-4 py-3 stat-num text-blue-400">{p.stats?.publish_queue || 0}</td>
                  <td className="px-4 py-3"><StatusPill status={p.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/projects/${p.id}`} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                      Open <ArrowRight className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
